import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FreightsService } from 'service/FreightsService';
import { FREIGHTS } from 'helpers/routes';

import TablesConstructor from 'components/Tables/TablesConstructor';
import BetTableData from 'components/Tables/BetTableData';
import CargoInfoTableData from 'components/Tables/CargoInfoTableData';
import RoutesTableData from 'components/Tables/RoutesTableData';
import ExchangeFreightPopup from 'components/FreightComponents/ExchangePopupFreight';
import FreigtTransportData from 'components/Tables/FreigtTransportData';
import LoadingType from 'components/Tables/LoadingType';
import PublicationDateData from 'components/Tables/PublicationDateData';
import ContactsTableData from 'components/Tables/ContactsTableData';
import StatusTableData from 'components/Tables/StatusTableData';

export default function FreightsTable({
  data,
  withItemsCount = false,
  updateData,
  selectedId,
  onSelect,
  searchCoordinates,
  showStatusColumn = false,
  isLoading = false,
  showAddButton = true,
}) {
  const authenticated = useSelector((state) => state.auth.authenticated);

  const { t } = useTranslation();

  const labels = useMemo(
    () =>
      [
        ...(showStatusColumn
          ? [
              {
                name: 'status',
                label: t('cargo.allTable.status'),
                data: ['status'],
                component: StatusTableData,
              },
            ]
          : []),
        {
          name: 'route_loading',
          label: t('cargo.allTable.uploading'),
          data: ['loading_address', 'loading_from', 'loading_to', 'isExpired'],
          component: RoutesTableData,
        },
        {
          name: 'route_unloading',
          label: t('air.table.route_unloading'),
          data: ['unloading_address', 'unloading_from', 'unloading_to'],
          component: RoutesTableData,
        },
        {
          name: 'transport_info',
          label: t('cargo.allTable.type_transport'),
          data: ['bodytypes', 'bodytype_additional', 'hitch', 'air_passage', 'conics'],
          component: FreigtTransportData,
        },
        {
          name: 'cargo_info',
          label: t('cargo.allTable.cargo_info'),
          data: [
            'designation',
            'cargo_designations_additional',
            'weight',
            'volume',
            'length',
            'width',
            'height',
            'adr_captions',
            'adr_items',
            'notes',
            'route_distance_meters',
          ],
          component: CargoInfoTableData,
        },
        {
          name: 'loading_type',
          label: t('transport.table.loadingType'),
          data: ['loading_types', 'conics', 'gidrolift'],
          component: LoadingType,
        },
        {
          name: 'bet',
          label: t('air.table.bet'),
          data: ['price', 'payment_type', 'payment_rate_type', 'currency', 'bank_days', 'is_vat_included'],
          component: BetTableData,
        },
        {
          name: 'publication_date',
          label: t('transport.table.publicationDate'),
          data: ['created_at', 'last_up_to_top_at'],
          component: PublicationDateData,
        },
        ...(authenticated
          ? [
              {
                name: 'contacts',
                label: t('air.table.contacts'),
                data: ['user', 'company', 'company_name', 'photo', 'company_logo_url_small', 'company_logo_url', 'ratings', 'paymentsSummary'],
                component: (props) => <ContactsTableData url={FREIGHTS.getContacts} {...props} />,
                topAlign: true,
              },
            ]
          : []),
      ].filter(Boolean),
    [t, authenticated, showStatusColumn],
  );

  return (
    <>
      <TablesConstructor
        data={data}
        labels={labels}
        resultsCountShow={withItemsCount}
        InfoComponent={ExchangeFreightPopup}
        service={FreightsService}
        updateData={updateData}
        urlToAdd={FREIGHTS.add}
        noDataMessage={t('cargo.allTable.if_no_cargoes')}
        selectedId={selectedId}
        onSelect={onSelect}
        searchCoordinates={searchCoordinates}
        isLoading={isLoading}
        showAddButton={showAddButton}
      />
    </>
  );
}

FreightsTable.propTypes = {
  data: PropTypes.object.isRequired,
  withItemsCount: PropTypes.bool,
  updateData: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func,
  showStatusColumn: PropTypes.bool,
  isLoading: PropTypes.bool,
};
